package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import com.advaita.cloud.drive.ui.store.DriveView
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun Breadcrumb(path: String, onNavigate: (String) -> Unit, onSearchClick: (() -> Unit)? = null) {
    val segments = buildSegments(path)

    Div(attrs = { classes("breadcrumb") }) {
        Div(attrs = { classes("crumbs") }) {
            segments.forEachIndexed { index, (label, segPath) ->
                if (index > 0) {
                    Span(attrs = { classes("sep") }) { Text(">") }
                }
                Span(attrs = {
                    attr("data-tip", "Go to $segPath")
                    onClick { onNavigate(segPath) }
                }) {
                    Text(label)
                }
            }
        }
        // Search lives on the drive bar, not the app toolbar: it scopes to the
        // folder the breadcrumb shows, so it sits beside what it searches.
        // Optional because this breadcrumb is reused inside the curated
        // Run-workflow folder picker, which has no search.
        if (onSearchClick != null) {
            Button(attrs = {
                classes("btn", "icon")
                attr("data-tip", "Search files by name (Ctrl/Cmd-K)")
                onClick { onSearchClick() }
            }) {
                Icon(IconName.SEARCH)
            }
        }
    }
}

/**
 * ADV-1330 F3: the two lists the workspace can show. A tab row, above the breadcrumb, so the
 * breadcrumb keeps meaning "where in this list".
 */
@Composable
fun ViewTabs(view: DriveView, onMyDrive: () -> Unit, onShared: () -> Unit, onSharedByMe: () -> Unit) {
    Div(attrs = { classes("view-tabs"); attr("role", "tablist") }) {
        listOf(
            DriveView.MY_DRIVE to "My Drive",
            DriveView.SHARED to "Shared with me",
            DriveView.SHARED_BY_ME to "Shared by me",
        ).forEach { (v, label) ->
            Button(attrs = {
                classes("view-tab")
                if (v == view) classes("active")
                attr("role", "tab")
                attr("aria-selected", (v == view).toString())
                onClick {
                    when (v) {
                        DriveView.MY_DRIVE -> onMyDrive()
                        DriveView.SHARED -> onShared()
                        DriveView.SHARED_BY_ME -> onSharedByMe()
                    }
                }
            }) { Text(label) }
        }
    }
}

/**
 * ADV-1330 F3: the breadcrumb of Shared with me. [onNavigate] takes a depth, 0 for the roots.
 * [showRoot] false (the Run workflow picker in a shared folder) leaves out "Shared with me", so
 * the first crumb is the shared root, at depth 1.
 */
@Composable
fun SharedBreadcrumb(
    trail: List<String>,
    onNavigate: (Int) -> Unit,
    showRoot: Boolean = true,
    /** ADV-1330: the search icon, as My Drive's [Breadcrumb] has it. Null hides it (the roots). */
    onSearchClick: (() -> Unit)? = null,
) {
    Div(attrs = { classes("breadcrumb") }) {
        Div(attrs = { classes("crumbs") }) {
            // Without the root crumb, the first crumb is the shared root, at depth 1.
            val first = if (showRoot) 0 else 1
            val crumbs = (if (showRoot) listOf("Shared with me") else emptyList()) + trail
            crumbs.forEachIndexed { index, label ->
                val depth = index + first
                if (index > 0) Span(attrs = { classes("sep") }) { Text(">") }
                // A button, so Tab reaches it and Enter or Space walks back to it.
                Button(attrs = {
                    classes("crumb-btn")
                    attr("type", "button")
                    if (index == crumbs.lastIndex) attr("aria-current", "location")
                    onClick { onNavigate(depth) }
                }) { Text(label) }
            }
        }
        if (onSearchClick != null) {
            Button(attrs = {
                classes("btn", "icon")
                attr("data-tip", "Search files by name (Ctrl/Cmd-K)")
                onClick { onSearchClick() }
            }) {
                Icon(IconName.SEARCH)
            }
        }
    }
}

private fun buildSegments(path: String): List<Pair<String, String>> {
    val segments = mutableListOf<Pair<String, String>>()
    segments.add("My Drive" to "/")

    if (path == "/" || path.isBlank()) return segments

    val parts = path.trim('/').split("/").filter { it.isNotBlank() }
    var cumulative = ""
    for (part in parts) {
        cumulative += "/$part"
        segments.add(part to cumulative)
    }
    return segments
}
